import { Button, Modal, ModalFooter } from "@/components/ui";
import { PresenceImpactStayList } from "@/components/device/PresenceImpactStayList";
import type { DeviceStructureLeaveImpact } from "@/types";

type StructureLeaveConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  intro: string;
  currentHeading: string;
  futureHeading: string;
  futureDiscardNote: string;
  currentEndedNote: string;
  emptyImpactMessage: string;
  confirmText: string;
  cancelText: string;
  closeLabel: string;
  impact: DeviceStructureLeaveImpact | null;
  isLoadingImpact?: boolean;
  isConfirming?: boolean;
};

export function StructureLeaveConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  intro,
  currentHeading,
  futureHeading,
  futureDiscardNote,
  currentEndedNote,
  emptyImpactMessage,
  confirmText,
  cancelText,
  closeLabel,
  impact,
  isLoadingImpact = false,
  isConfirming = false,
}: StructureLeaveConfirmModalProps) {
  const hasImpact = Boolean(impact?.has_impact);
  const busy = isLoadingImpact || isConfirming;
  const current = impact?.current ?? [];
  const future = impact?.future ?? [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeLabel={closeLabel}
      title={title}
      size="md"
      priority="high"
      role="alertdialog"
      showCloseButton={!busy}
      closeOnEscape={!busy}
      dismissOnBackdrop={!busy}
      footer={
        <ModalFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            {cancelText}
          </Button>
          <Button
            variant="error"
            onClick={onConfirm}
            isLoading={isConfirming}
            disabled={isLoadingImpact}
          >
            {confirmText}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        <p className="text-[14px] text-surface-700">{intro}</p>
        {hasImpact ? (
          <>
            {current.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-[13px] font-semibold text-surface-900">
                  {currentHeading}
                </h4>
                <p className="text-[12px] text-surface-500">{currentEndedNote}</p>
                <PresenceImpactStayList items={current} />
              </div>
            ) : null}
            {future.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-[13px] font-semibold text-surface-900">
                  {futureHeading}
                </h4>
                <p className="text-[12px] text-surface-500">{futureDiscardNote}</p>
                <PresenceImpactStayList items={future} />
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-[13px] text-surface-500">{emptyImpactMessage}</p>
        )}
      </div>
    </Modal>
  );
}
